import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   SpecificationAnalysisTab.jsx
   ----------------------------------------------------------------------------
   ZONE B1 — ANALYSIS WORKSPACE

   RÔLE
   - explorer le corpus de spécifications du projet
   - qualifier leur état réel
   - lire la couverture fonctionnelle
   - préparer la consolidation future dans Review

   STRUCTURE
   1. Contexte d’analyse + filtres
   2. Liste scrollable des spécifications (colonne gauche)
   3. Fiche analytique en vis-à-vis (colonne droite)
   4. Couverture fonctionnelle avec modes d’édition légers

   NOTE
   - données mockées
   - structure prête pour raccordement backend / PostgreSQL
============================================================================ */

/* ============================================================================
   TOKENS
============================================================================ */

const T = {
  bg: theme?.colors?.background?.surface || "#ffffff",
  panel: theme?.colors?.background?.panel || "#f1f3f6",
  hover: theme?.colors?.background?.hover || "#f7f9fc",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",

  text: theme?.colors?.text?.primary || "#164D63",
  body: theme?.colors?.text?.body || "#3F5D6E",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  muted: theme?.colors?.text?.muted || "#94a3b8",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",

  green: theme?.colors?.text?.green || "#1A8C74",
  orange: theme?.colors?.text?.secondary || "#B8621B",
  red: theme?.Generic?.red?.dark?.bg || "#C2551A",
  blue: theme?.colors?.text?.primary || "#164D63",

  pastelBlue: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  pastelGreen: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  pastelYellow: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  pastelOrange: theme?.Generic?.orange?.pastel?.bg || "#F8ECE5",
  pastelGrey: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",
  pastelRed: theme?.Generic?.red?.pastel?.bg || "#F7E9E9",
};

/* ============================================================================
   DATA MOCK
   ----------------------------------------------------------------------------
   Volume volontairement augmenté pour tester le scroll de la liste.
============================================================================ */

const SPEC_ROWS = [
  {
    id: "sp-001",
    title: "Pression nominale de refoulement",
    ouvrage: "Station de pompage",
    systeme: "Pompage",
    sousSysteme: "Pompes de service",
    lot: "Lot Hydraulique",
    discipline: "Hydraulique",
    fonction: "Performance hydraulique",
    maturityStatus: "Validée",
    technicalStatus: "Maîtrisée",
    projectImpact: "Sous contrôle",
    note: "Cohérente avec le besoin de service et les hypothèses de débit.",
  },
  {
    id: "sp-002",
    title: "Asservissement pompe / armoire",
    ouvrage: "Station de pompage",
    systeme: "Commande",
    sousSysteme: "Pompe / armoire",
    lot: "Lot Électrique",
    discipline: "Électricité",
    fonction: "Continuité de service",
    maturityStatus: "À consolider",
    technicalStatus: "Partielle",
    projectImpact: "Critique",
    note: "La logique de commande reste à confirmer entre packages.",
  },
  {
    id: "sp-003",
    title: "Retour d’état instrumentation process",
    ouvrage: "Station de pompage",
    systeme: "Instrumentation",
    sousSysteme: "Capteurs de retour",
    lot: "Lot Automatisme",
    discipline: "Automatisme",
    fonction: "Suivi d’exploitation",
    maturityStatus: "En cours",
    technicalStatus: "Instable",
    projectImpact: "Critique",
    note: "Les états remontés restent incomplets pour l’exploitation.",
  },
  {
    id: "sp-004",
    title: "Protection anticorrosion interne",
    ouvrage: "Réservoir",
    systeme: "Protection structurelle",
    sousSysteme: "Revêtement interne",
    lot: "Lot Structure",
    discipline: "Structure",
    fonction: "Durabilité",
    maturityStatus: "À consolider",
    technicalStatus: "Partielle",
    projectImpact: "Sous contrôle",
    note: "Les références techniques doivent être harmonisées.",
  },
  {
    id: "sp-005",
    title: "Accessibilité maintenance locale",
    ouvrage: "Bâtiment technique",
    systeme: "Exploitation",
    sousSysteme: "Accès maintenance",
    lot: "Lot Exploitation",
    discipline: "Exploitation",
    fonction: "Maintenance",
    maturityStatus: "Validée",
    technicalStatus: "Maîtrisée",
    projectImpact: "Neutre",
    note: "La lecture d’usage est cohérente avec les besoins d’accès.",
  },
  {
    id: "sp-006",
    title: "Réservation de traversée de dalle",
    ouvrage: "Bâtiment technique",
    systeme: "Interfaces GC",
    sousSysteme: "Traversées",
    lot: "Lot Génie civil",
    discipline: "Génie civil",
    fonction: "Coordination technique",
    maturityStatus: "En cours",
    technicalStatus: "Partielle",
    projectImpact: "Critique",
    note: "La coordination multi-lots doit encore être verrouillée.",
  },
  {
    id: "sp-007",
    title: "Indice de protection IP65 armoire moteur",
    ouvrage: "Station de pompage",
    systeme: "Électricité",
    sousSysteme: "Armoire moteur",
    lot: "Lot Électrique",
    discipline: "Électricité",
    fonction: "Sécurité exploitation",
    maturityStatus: "Validée",
    technicalStatus: "Maîtrisée",
    projectImpact: "Sous contrôle",
    note: "Exigence claire et directement exploitable.",
  },
  {
    id: "sp-008",
    title: "Alimentation secourue des automatismes",
    ouvrage: "Station de pompage",
    systeme: "Automatisme",
    sousSysteme: "Secours énergie",
    lot: "Lot Automatisme",
    discipline: "Électricité",
    fonction: "Continuité de service",
    maturityStatus: "À consolider",
    technicalStatus: "Instable",
    projectImpact: "Bloquant",
    note: "Le schéma de secours conditionne la robustesse du dispositif.",
  },
  {
    id: "sp-009",
    title: "Drainage périphérique du réservoir",
    ouvrage: "Réservoir",
    systeme: "Drainage",
    sousSysteme: "Collecte périphérique",
    lot: "Lot Génie civil",
    discipline: "Génie civil",
    fonction: "Protection ouvrage",
    maturityStatus: "Non traitée",
    technicalStatus: "Non définie",
    projectImpact: "Critique",
    note: "Le besoin est identifié mais la spécification reste absente.",
  },
  {
    id: "sp-010",
    title: "Ventilation haute du local électrique",
    ouvrage: "Bâtiment technique",
    systeme: "Ventilation",
    sousSysteme: "Grilles hautes",
    lot: "Lot Bâtiment",
    discipline: "Architecture",
    fonction: "Protection équipements",
    maturityStatus: "En cours",
    technicalStatus: "Partielle",
    projectImpact: "Sous contrôle",
    note: "Le principe est défini mais les sections libres restent à confirmer.",
  },
  {
    id: "sp-011",
    title: "Protection foudre de l’armoire générale",
    ouvrage: "Station de pompage",
    systeme: "Électricité",
    sousSysteme: "Protection surtension",
    lot: "Lot Électrique",
    discipline: "Électricité",
    fonction: "Sécurité exploitation",
    maturityStatus: "À consolider",
    technicalStatus: "Partielle",
    projectImpact: "Critique",
    note: "Le niveau de protection et la coordination SPD restent à stabiliser.",
  },
  {
    id: "sp-012",
    title: "By-pass de maintenance sur ligne principale",
    ouvrage: "Station de pompage",
    systeme: "Hydraulique",
    sousSysteme: "By-pass",
    lot: "Lot Hydraulique",
    discipline: "Hydraulique",
    fonction: "Maintenance",
    maturityStatus: "En cours",
    technicalStatus: "Instable",
    projectImpact: "Critique",
    note: "Le principe est discuté mais la configuration finale reste ouverte.",
  },
  {
    id: "sp-013",
    title: "Étanchéité des pénétrations en voile enterré",
    ouvrage: "Réservoir",
    systeme: "Interfaces structurelles",
    sousSysteme: "Traversées réseau",
    lot: "Lot Structure",
    discipline: "Structure",
    fonction: "Étanchéité",
    maturityStatus: "À consolider",
    technicalStatus: "Partielle",
    projectImpact: "Critique",
    note: "Les détails de traitement de traversée doivent être consolidés.",
  },
  {
    id: "sp-014",
    title: "Accès sécurisé au poste de dosage",
    ouvrage: "Bâtiment process",
    systeme: "Exploitation",
    sousSysteme: "Circulations",
    lot: "Lot Exploitation",
    discipline: "Exploitation",
    fonction: "Sécurité exploitation",
    maturityStatus: "Validée",
    technicalStatus: "Maîtrisée",
    projectImpact: "Sous contrôle",
    note: "Les accès et dégagements sont conformes aux hypothèses d’usage.",
  },
];

const INITIAL_COVERAGE_ROWS = [
  {
    id: "f1",
    functionLabel: "Sécurité exploitation",
    relatedSpecs: [
      "Indice de protection IP65 armoire moteur",
      "Arrêt d’urgence accessible",
      "Mise à la terre équipement",
    ],
    coveredCount: 2,
    totalCount: 3,
    technicalStatus: "Partielle",
    maturityStatus: "En cours",
  },
  {
    id: "f2",
    functionLabel: "Continuité de service",
    relatedSpecs: [
      "Redondance pompe 2×100%",
      "Alimentation secourue des automatismes",
      "Asservissement pompe / armoire",
    ],
    coveredCount: 1,
    totalCount: 3,
    technicalStatus: "Instable",
    maturityStatus: "À consolider",
  },
  {
    id: "f3",
    functionLabel: "Maintenance",
    relatedSpecs: [
      "Accessibilité maintenance locale",
      "Dégagement frontal 900 mm",
      "Trappe accès pompe",
    ],
    coveredCount: 1,
    totalCount: 3,
    technicalStatus: "Partielle",
    maturityStatus: "En cours",
  },
  {
    id: "f4",
    functionLabel: "Protection ouvrage",
    relatedSpecs: [
      "Drainage périphérique du réservoir",
      "Protection anticorrosion interne",
    ],
    coveredCount: 1,
    totalCount: 2,
    technicalStatus: "Partielle",
    maturityStatus: "Non traitée",
  },
];

/* ============================================================================
   FILTER OPTIONS
============================================================================ */

const MATURITY_OPTIONS = [
  { id: "all", label: "Tous" },
  { id: "Non traitée", label: "Non traitées" },
  { id: "En cours", label: "En cours" },
  { id: "À consolider", label: "À consolider" },
  { id: "Validée", label: "Validées" },
];

const TECHNICAL_OPTIONS = [
  { id: "all", label: "Toutes" },
  { id: "Non définie", label: "Non définies" },
  { id: "Instable", label: "Instables" },
  { id: "Partielle", label: "Partielles" },
  { id: "Maîtrisée", label: "Maîtrisées" },
];

const IMPACT_OPTIONS = [
  "Neutre",
  "Sous contrôle",
  "Critique",
  "Bloquant",
];

const ACTION_OPTIONS = [
  "À confirmer",
  "À arbitrer",
  "À compléter",
  "À coordonner",
];

const PILOT_OPTIONS = [
  "Lead Civil",
  "Lead Structural",
  "Lead Hydraulic",
  "Lead Electrical",
  "Lead Automation",
  "Lead Architecture",
  "Lead Operations",
];

/* ============================================================================
   HELPERS
============================================================================ */

const getMaturityStyle = (value) => {
  const map = {
    "Non traitée": {
      bg: T.pastelGrey,
      text: T.body,
      border: T.border,
    },
    "En cours": {
      bg: T.pastelBlue,
      text: T.blue,
      border: T.borderStrong,
    },
    "À consolider": {
      bg: T.pastelYellow,
      text: T.orange,
      border: theme?.Generic?.yellow?.pastel?.border || "#D8C6A6",
    },
    Validée: {
      bg: T.pastelGreen,
      text: T.green,
      border: theme?.Generic?.green?.pastel?.border || "#B9CEC4",
    },
  };
  return map[value] || { bg: T.pastelGrey, text: T.body, border: T.border };
};

const getTechnicalStyle = (value) => {
  const map = {
    "Non définie": {
      bg: T.pastelGrey,
      text: T.body,
      bar: T.borderStrong,
    },
    Instable: {
      bg: T.pastelRed,
      text: T.caneva,
      bar: T.caneva,
    },
    Partielle: {
      bg: T.pastelYellow,
      text: T.orange,
      bar: T.orange,
    },
    "Maîtrisée": {
      bg: T.pastelGreen,
      text: T.green,
      bar: T.green,
    },
  };
  return map[value] || { bg: T.pastelGrey, text: T.body, bar: T.borderStrong };
};

const getImpactStyle = (value) => {
  const map = {
    Bloquant: {
      text: T.caneva,
    },
    Critique: {
      text: T.orange,
    },
    "Sous contrôle": {
      text: T.blue,
    },
    Neutre: {
      text: T.soft,
    },
  };
  return map[value] || { text: T.soft };
};

const getCoverageLabel = (coveredCount, totalCount) => {
  if (coveredCount <= 0) return "Non couverte";
  if (coveredCount < totalCount) return "Partielle";
  return "Couverte";
};

const getCoverageStyle = (coveredCount, totalCount) => {
  const label = getCoverageLabel(coveredCount, totalCount);
  if (label === "Non couverte") {
    return { text: T.caneva };
  }
  if (label === "Partielle") {
    return { text: T.orange };
  }
  return { text: T.green };
};

const getAutoPilot = (discipline) => {
  const map = {
    "Génie civil": "Lead Civil",
    Structure: "Lead Structural",
    Hydraulique: "Lead Hydraulic",
    Électricité: "Lead Electrical",
    Automatisme: "Lead Automation",
    Architecture: "Lead Architecture",
    Exploitation: "Lead Operations",
  };

  return map[discipline] || "Lead Civil";
};

const getDefaultAction = (impact) => {
  if (impact === "Bloquant") return "À arbitrer";
  if (impact === "Critique") return "À coordonner";
  return "À confirmer";
};

const buildInitialSpecEditState = () => {
  return SPEC_ROWS.reduce((acc, row) => {
    acc[row.id] = {
      maturityStatus: row.maturityStatus,
      technicalStatus: row.technicalStatus,
      projectImpact: row.projectImpact,
      pilot: getAutoPilot(row.discipline),
      action: getDefaultAction(row.projectImpact),
      note: row.note,
    };
    return acc;
  }, {});
};

/* ============================================================================
   UI ATOMS
============================================================================ */

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: theme?.fontSizes?.sm || "0.90rem",
      fontWeight: theme?.fontWeights?.medium || 500,
      lineHeight: 1.15,
      color: T.soft,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const FilterPill = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      height: 28,
      padding: "0 10px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      border: active ? `1px solid ${T.corelia}` : `1px solid ${T.border}`,
      background: active ? T.pastelBlue : "#ffffff",
      color: active ? T.corelia : T.body,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.76rem",
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: "nowrap",
    }}
  >
    <span>{label}</span>
    {typeof count === "number" ? (
      <span style={{ color: T.verySoft, fontWeight: 400 }}>{count}</span>
    ) : null}
  </button>
);

const MicroTag = ({ label, style }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 8px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      background: style.bg,
      color: style.text,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.70rem",
      lineHeight: 1,
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </div>
);

const FieldLabel = ({ children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.72rem",
      lineHeight: 1.1,
      color: T.soft,
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

const radioTextStyle = {
  fontFamily: theme?.fonts?.body || "inherit",
  fontSize: "0.75rem",
  lineHeight: 1.2,
  color: T.body,
};

const selectStyle = {
  ...theme.form.field,
  minHeight: 28,
  width: "100%",
  fontSize: "0.74rem",
  color: T.body,
  background: "#ffffff",
};

const textareaStyle = {
  ...theme.form.field,
  minHeight: 76,
  width: "100%",
  resize: "vertical",
  fontSize: "0.79rem",
  lineHeight: 1.45,
  color: T.body,
  background: "#ffffff",
  padding: "8px 10px",
};


const PANEL_HEIGHT = 600;
/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationAnalysisTab = ({ analysisSelection = null }) => {
  const [selectedMaturity, setSelectedMaturity] = useState("all");
  const [selectedTechnical, setSelectedTechnical] = useState("all");
  const [selectedRowId, setSelectedRowId] = useState(SPEC_ROWS[0]?.id || null);
  const [coverageRows, setCoverageRows] = useState(INITIAL_COVERAGE_ROWS);
  const [specEditState, setSpecEditState] = useState(buildInitialSpecEditState);

  const mergedRows = useMemo(() => {
    return SPEC_ROWS.map((row) => ({
      ...row,
      ...(specEditState[row.id] || {}),
    }));
  }, [specEditState]);

  const maturityCounts = useMemo(() => {
    return {
      all: mergedRows.length,
      "Non traitée": mergedRows.filter((r) => r.maturityStatus === "Non traitée").length,
      "En cours": mergedRows.filter((r) => r.maturityStatus === "En cours").length,
      "À consolider": mergedRows.filter((r) => r.maturityStatus === "À consolider").length,
      Validée: mergedRows.filter((r) => r.maturityStatus === "Validée").length,
    };
  }, [mergedRows]);

  const technicalCounts = useMemo(() => {
    return {
      all: mergedRows.length,
      "Non définie": mergedRows.filter((r) => r.technicalStatus === "Non définie").length,
      Instable: mergedRows.filter((r) => r.technicalStatus === "Instable").length,
      Partielle: mergedRows.filter((r) => r.technicalStatus === "Partielle").length,
      "Maîtrisée": mergedRows.filter((r) => r.technicalStatus === "Maîtrisée").length,
    };
  }, [mergedRows]);

  const filteredRows = useMemo(() => {
    return mergedRows.filter((row) => {
      const maturityMatch =
        selectedMaturity === "all" || row.maturityStatus === selectedMaturity;
      const technicalMatch =
        selectedTechnical === "all" || row.technicalStatus === selectedTechnical;
      return maturityMatch && technicalMatch;
    });
  }, [mergedRows, selectedMaturity, selectedTechnical]);

  const selectedRow = useMemo(() => {
    return (
      filteredRows.find((row) => row.id === selectedRowId) ||
      filteredRows[0] ||
      null
    );
  }, [filteredRows, selectedRowId]);

  const coverageSummary = useMemo(() => {
    const total = coverageRows.reduce((acc, row) => acc + row.totalCount, 0);
    const covered = coverageRows.reduce((acc, row) => acc + row.coveredCount, 0);
    return { total, covered };
  }, [coverageRows]);

  const buildContext = () => {
    if (!analysisSelection) return "";

    return [
      analysisSelection.axisLabel,
      analysisSelection.branchLabel,
      analysisSelection.familyLabel,
    ]
      .filter(Boolean)
      .join(" — ");
  };

  const updateCoverageRow = (rowId, field, value) => {
    setCoverageRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const updateSelectedSpec = (field, value) => {
    if (!selectedRow?.id) return;

    setSpecEditState((prev) => ({
      ...prev,
      [selectedRow.id]: {
        ...(prev[selectedRow.id] || {}),
        [field]: value,
      },
    }));
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: theme?.spacing?.lg || 20,
        boxSizing: "border-box",
      }}
    >

      
      {/* ============================================================
         ZONE 1 — CONTEXTE + FILTRES
      ============================================================ */}
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "0.76rem",
            color: T.soft,
            lineHeight: 1.2,
          }}
        >
          {buildContext()}
        </div>

        <div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    alignItems: "start",
  }}
>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            <div
              style={{
                minWidth: 90,
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.74rem",
                color: T.soft,
              }}
            >
              Avancement
            </div>

            {MATURITY_OPTIONS.map((item) => (
              <FilterPill
                key={item.id}
                label={item.label}
                count={maturityCounts[item.id]}
                active={selectedMaturity === item.id}
                onClick={() => setSelectedMaturity(item.id)}
              />
            ))}
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            <div
              style={{
                minWidth: 90,
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.74rem",
                color: T.soft,
              }}
            >
              Maîtrise
            </div>

            {TECHNICAL_OPTIONS.map((item) => (
              <FilterPill
                key={item.id}
                label={item.label}
                count={technicalCounts[item.id]}
                active={selectedTechnical === item.id}
                onClick={() => setSelectedTechnical(item.id)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ============================================================
         ZONE 2 — LISTE + FICHE EN VIS-À-VIS
      ============================================================ */}
      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "minmax(0, 60%) minmax(320px, 1fr)",
          gap: 16,
          alignItems: "stretch",
        }}
      >
        {/* --------------------------------------------------------
           COLONNE GAUCHE — LISTE SCROLLABLE
        -------------------------------------------------------- */}
        
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minWidth: 0,
          }}
        >
          <SectionTitle>Spécifications analysées</SectionTitle>

          <div
            style={{
              width: "100%",
              height: PANEL_HEIGHT,
              overflowY: "auto",
              overflowX: "hidden",
              border: `1px solid ${T.border}`,
              padding: "6px 8px 6px 0",
              boxSizing: "border-box",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {filteredRows.map((row) => {
                const active = selectedRow?.id === row.id;
                const technicalStyle = getTechnicalStyle(row.technicalStatus);
                const maturityStyle = getMaturityStyle(row.maturityStatus);
                const impactStyle = getImpactStyle(row.projectImpact);

                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setSelectedRowId(row.id)}
                    style={{
                      width: "100%",
                      background: active ? "#f6fafc" : "transparent",
                      border: `1px solid ${active ? T.borderStrong : "transparent"}`,
                      cursor: "pointer",
                      padding: "8px 8px 8px 0",
                      display: "grid",
                      gridTemplateColumns: "4px 1fr",
                      gap: 10,
                      alignItems: "stretch",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        width: 4,
                        background: technicalStyle.bar,
                      }}
                    />

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                        minWidth: 0,
                      }}
                    >
                      <div
                        style={{
                          fontFamily: theme?.fonts?.body || "inherit",
                          fontSize: "0.84rem",
                          lineHeight: 1.2,
                          color: T.text,
                          fontWeight: theme?.fontWeights?.medium || 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.title}
                      </div>

                      <div
                        style={{
                          fontFamily: theme?.fonts?.body || "inherit",
                          fontSize: "0.73rem",
                          lineHeight: 1.25,
                          color: T.body,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.ouvrage} — {row.systeme} — {row.sousSysteme}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <MicroTag
                          label={row.maturityStatus}
                          style={maturityStyle}
                        />
                        <MicroTag
                          label={row.technicalStatus}
                          style={technicalStyle}
                        />
                        <div
                          style={{
                            fontFamily: theme?.fonts?.body || "inherit",
                            fontSize: "0.71rem",
                            lineHeight: 1.1,
                            color: impactStyle.text,
                            fontWeight: 500,
                          }}
                        >
                          {row.projectImpact}
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr auto",
                          gap: 8,
                          alignItems: "start",
                        }}
                      >
                        <div
                          style={{
                            fontFamily: theme?.fonts?.body || "inherit",
                            fontSize: "0.72rem",
                            lineHeight: 1.25,
                            color: T.soft,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.note}
                        </div>

                        <div
                          style={{
                            fontFamily: theme?.fonts?.body || "inherit",
                            fontSize: "0.70rem",
                            lineHeight: 1.1,
                            color: T.verySoft,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.discipline}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------
           COLONNE DROITE — FICHE ANALYTIQUE
        -------------------------------------------------------- */}
        <div
          style={{
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <SectionTitle>Fiche analytique</SectionTitle>

          <div
  style={{
    width: "100%",
    height: PANEL_HEIGHT,
    border: `1px solid ${T.border}`,
    background: T.pastelGrey,
    padding: "14px 14px 12px 14px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 14,
  }}
>
            <div
              style={{
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.98rem",
                lineHeight: 1.3,
                color: T.text,
                fontWeight: theme?.fontWeights?.medium || 500,
              }}
            >
              {selectedRow?.title || "—"}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              {[
                { label: "Ouvrage", value: selectedRow?.ouvrage || "—" },
                { label: "Système", value: selectedRow?.systeme || "—" },
                { label: "Sous-système", value: selectedRow?.sousSysteme || "—" },
                { label: "Lot", value: selectedRow?.lot || "—" },
                { label: "Discipline", value: selectedRow?.discipline || "—" },
                { label: "Fonction couverte", value: selectedRow?.fonction || "—" },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <FieldLabel>{item.label}</FieldLabel>

                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.82rem",
                      lineHeight: 1.35,
                      color: T.body,
                    }}
                  >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {/* ------------------------------------------------------
               ÉDITION — AVANCEMENT
            ------------------------------------------------------ */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingTop: 2,
                borderTop: `1px solid ${T.border}`,
              }}
            >
              <FieldLabel>Avancement</FieldLabel>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                {["Non traitée", "En cours", "À consolider", "Validée"].map((option) => (
                  <label
                    key={option}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="selected-spec-maturity"
                      checked={selectedRow?.maturityStatus === option}
                      onChange={() => updateSelectedSpec("maturityStatus", option)}
                      style={{ accentColor: T.corelia, cursor: "pointer" }}
                    />
                    <span style={radioTextStyle}>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ------------------------------------------------------
               ÉDITION — MAÎTRISE
            ------------------------------------------------------ */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <FieldLabel>Maîtrise</FieldLabel>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                {["Non définie", "Instable", "Partielle", "Maîtrisée"].map((option) => (
                  <label
                    key={option}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="selected-spec-technical"
                      checked={selectedRow?.technicalStatus === option}
                      onChange={() => updateSelectedSpec("technicalStatus", option)}
                      style={{ accentColor: T.corelia, cursor: "pointer" }}
                    />
                    <span style={radioTextStyle}>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ------------------------------------------------------
               ÉDITION — IMPACT PROJET
            ------------------------------------------------------ */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <FieldLabel>Impact projet</FieldLabel>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                {IMPACT_OPTIONS.map((option) => (
                  <label
                    key={option}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="selected-spec-impact"
                      checked={selectedRow?.projectImpact === option}
                      onChange={() => updateSelectedSpec("projectImpact", option)}
                      style={{ accentColor: T.corelia, cursor: "pointer" }}
                    />
                    <span style={radioTextStyle}>{option}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* ------------------------------------------------------
               ÉDITION — PILOTE
            ------------------------------------------------------ */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                alignItems: "start",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <FieldLabel>Pilote</FieldLabel>
                <select
                  value={selectedRow?.pilot || ""}
                  onChange={(e) => updateSelectedSpec("pilot", e.target.value)}
                  style={selectStyle}
                >
                  {PILOT_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <FieldLabel>Action attendue</FieldLabel>
                <select
                  value={selectedRow?.action || ""}
                  onChange={(e) => updateSelectedSpec("action", e.target.value)}
                  style={selectStyle}
                >
                  {ACTION_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ------------------------------------------------------
               LECTURE / NOTE
            ------------------------------------------------------ */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingTop: 4,
                borderTop: `1px solid ${T.border}`,
              }}
            >
              <FieldLabel>Lecture analytique</FieldLabel>

              <textarea
                value={selectedRow?.note || ""}
                onChange={(e) => updateSelectedSpec("note", e.target.value)}
                style={textareaStyle}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ============================================================
         ZONE 4 — COUVERTURE FONCTIONNELLE
      ============================================================ */}
      <div
        style={{
          width: "100%",
          border: `1px solid ${T.border}`,
          padding: "12px 12px 10px 12px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <SectionTitle>Couverture fonctionnelle</SectionTitle>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "22% 34% 14% 14% 16%",
            gap: 10,
            alignItems: "start",
          }}
        >
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.77rem",
              color: T.soft,
              fontWeight: 500,
            }}
          >
            Fonction
          </div>
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.77rem",
              color: T.soft,
              fontWeight: 500,
            }}
          >
            Spécifications associées
          </div>
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.77rem",
              color: T.soft,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Couverture
          </div>
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.77rem",
              color: T.soft,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Maîtrise
          </div>
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.77rem",
              color: T.soft,
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Avancement
          </div>

          {coverageRows.map((row, index) => {
            const coverageStyle = getCoverageStyle(row.coveredCount, row.totalCount);

            return (
              <React.Fragment key={row.id}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.80rem",
                      lineHeight: 1.2,
                      color: T.text,
                      fontWeight: 500,
                    }}
                  >
                    {row.functionLabel}
                  </div>

                  <div
                    style={{
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.71rem",
                      lineHeight: 1.1,
                      color: T.soft,
                    }}
                  >
                    {row.coveredCount} / {row.totalCount} traitées
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  {row.relatedSpecs.map((spec) => (
                    <div
                      key={spec}
                      style={{
                        fontFamily: theme?.fonts?.body || "inherit",
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        color: T.body,
                      }}
                    >
                      {spec}
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    paddingTop: 2,
                    fontFamily: theme?.fonts?.body || "inherit",
                    fontSize: "0.74rem",
                    lineHeight: 1.2,
                    color: coverageStyle.text,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  {getCoverageLabel(row.coveredCount, row.totalCount)}
                </div>

                {/* ------------------------------------------------------
                   MODE D'ÉDITION 1 — MAÎTRISE
                ------------------------------------------------------ */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: 2,
                  }}
                >
                  <select
                    value={row.technicalStatus}
                    onChange={(e) =>
                      updateCoverageRow(row.id, "technicalStatus", e.target.value)
                    }
                    style={{
                      ...theme.form.field,
                      minHeight: 28,
                      width: "100%",
                      maxWidth: 128,
                      fontSize: "0.73rem",
                      color: T.body,
                      background: "#ffffff",
                    }}
                  >
                    <option value="Non définie">Non définie</option>
                    <option value="Instable">Instable</option>
                    <option value="Partielle">Partielle</option>
                    <option value="Maîtrisée">Maîtrisée</option>
                  </select>
                </div>

                {/* ------------------------------------------------------
                   MODE D'ÉDITION 2 — AVANCEMENT
                ------------------------------------------------------ */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    paddingTop: 2,
                  }}
                >
                  <select
                    value={row.maturityStatus}
                    onChange={(e) =>
                      updateCoverageRow(row.id, "maturityStatus", e.target.value)
                    }
                    style={{
                      ...theme.form.field,
                      minHeight: 28,
                      width: "100%",
                      maxWidth: 128,
                      fontSize: "0.73rem",
                      color: T.body,
                      background: "#ffffff",
                    }}
                  >
                    <option value="Non traitée">Non traitée</option>
                    <option value="En cours">En cours</option>
                    <option value="À consolider">À consolider</option>
                    <option value="Validée">Validée</option>
                  </select>
                </div>

                {index < coverageRows.length - 1 && (
                  <div
                    style={{
                      gridColumn: "1 / span 5",
                      height: 1,
                      background: T.border,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 2,
          }}
        >
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.73rem",
              lineHeight: 1.1,
              color: T.soft,
            }}
          >
            Zone de qualification légère pour alimenter les statuts du moteur Corelia
          </div>

          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.73rem",
              lineHeight: 1.1,
              color: T.body,
            }}
          >
            Couverture globale : {coverageSummary.covered} / {coverageSummary.total}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecificationAnalysisTab;